import { ContainerStackScreen } from "@Components/ContainerStackScreen"
import { BreeSerifText } from "@Components/CustomText/BreeSerifText"
import { OpenSansText } from "@Components/CustomText/OpenSansText"
import { Spacer } from "@Components/Space"
import { baseColors } from "@Constants/baseStyles"
import React, { useEffect, useState } from "react"
import { View, TouchableOpacity, StyleSheet, TextInput, FlatList, ScrollView, Platform, Dimensions } from "react-native"
import { ActivityIndicator, Divider } from "react-native-paper"
import { ShowToast } from "@Components/ToastNotification/ToastNotification"
import { message } from "@Constants/constant"
import { MicroGoalService } from "@Services/MicroGoal.Service"
import { Button } from "@Components/CustomButton/Button"
import CustomSimpleModal from "@Components/Modal/CustomSimpleModal/CustomSimpleModal"
import { RFValue } from "react-native-responsive-fontsize"
import { MaterialIcons } from "@expo/vector-icons"
import MultiSlider from "@ptomasroos/react-native-multi-slider"
import RenderHTML from "react-native-render-html"
import { useWindowDimensions } from "react-native"
import './reactQuill.css'

let RichEditor, RichToolbar, actions, ReactQuill
const isWeb = Platform.OS === "web"
if (isWeb) {
    ReactQuill = require("react-quill").default || require("react-quill")
    require("react-quill/dist/quill.snow.css")
    const quillStyle = document.createElement("style")
    quillStyle.textContent = ".ql-editor { min-height: 100px; max-height: 150px; overflow-y: auto; line-height: 1; }"
    if (!document.querySelector("[data-quill-custom-style]")) {
        quillStyle.setAttribute("data-quill-custom-style", "true")
        document.head.appendChild(quillStyle)
    }
} else {
    const pellEditor = require("react-native-pell-rich-editor")
    RichEditor = pellEditor.RichEditor
    RichToolbar = pellEditor.RichToolbar
    actions = pellEditor.actions
}

const quillModules = {
    toolbar: [
        ["bold", "italic", "underline"],
        [{ list: "bullet" }, { list: "ordered" }]
    ]
}

const screenHeight = Dimensions.get("window").height

const STATUSES = ["Pas atteint", "En cours", "Partiellement atteint", "Quasi atteint", "Atteint"]

const statusColors = {
    "Pas atteint": "purple",
    "En cours": "red",
    "Partiellement atteint": "orange",
    "Quasi atteint": "yellow",
    "Atteint": "green"
}

const sliderToStatus = (value) => STATUSES[value - 1]
const statusToSlider = (status) => {
    const index = STATUSES.indexOf(status)
    return index >= 0 ? index + 1 : 1
}
const sliderColor = (value) => {
    const colors = ["purple", "red", "orange", "yellow", "green"]
    return colors[value - 1] || "#E74C3C"
}

export const MicroGoals = ({ navigation }) => {
    const { width } = useWindowDimensions()
    const editEditorRef = React.useRef()
    const createEditorRef = React.useRef()

    const [microGoals, setMicroGoals] = useState([])
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    const [editModalVisible, setEditModalVisible] = useState(false)
    const [createModalVisible, setCreateModalVisible] = useState(false)
    const [statusModalVisible, setStatusModalVisible] = useState(false)

    const [deleteModalVisible, setDeleteModalVisible] = useState(false)

    const [selectedGoal, setSelectedGoal] = useState(null)
    const [sliderValue, setSliderValue] = useState(1)
    const [editDescription, setEditDescription] = useState("")
    const [editRemarks, setEditRemarks] = useState("")
    const [newDescription, setNewDescription] = useState("")
    const [newRemarks, setNewRemarks] = useState("")

    const fetchMicroGoals = () => {
        setLoading(true)
        MicroGoalService.getList()
            .then((response) => {
                const data = response.data?.data?.items
                if (data && data instanceof Array) {
                    setMicroGoals(data)
                }
                setLoading(false)
            })
            .catch((e) => {
                console.log({ microGoalsError: e })
                setLoading(false)
                ShowToast({ type: "error", message: message.serverError })
            })
    }

    useEffect(() => {
        fetchMicroGoals()
    }, [])

    const handleEdit = (goal) => {
        setSelectedGoal(goal)
        setEditDescription(goal.description)
        setEditRemarks(goal.remarks || "")
        setEditModalVisible(true)
    }

    const handleSaveEdit = () => {
        if (!editDescription.trim()) return
        setSaving(true)
        MicroGoalService.update(selectedGoal.id, { description: editDescription.trim(), remarks: editRemarks })
            .then(() => {
                ShowToast({ type: "success", message: "Micro-objectif modifié avec succès" })
                setEditModalVisible(false)
                setSelectedGoal(null)
                fetchMicroGoals()
            })
            .catch((e) => {
                console.log({ updateError: e })
                ShowToast({ type: "error", message: message.serverError })
            })
            .finally(() => setSaving(false))
    }

    const handleDelete = (goal) => {
        setSelectedGoal(goal)
        setDeleteModalVisible(true)
    }

    const confirmDelete = () => {
        if (!selectedGoal) return
        setSaving(true)
        MicroGoalService.remove(selectedGoal.id)
            .then(() => {
                ShowToast({ type: "success", message: "Micro-objectif supprimé" })
                setDeleteModalVisible(false)
                setSelectedGoal(null)
                fetchMicroGoals()
            })
            .catch((e) => {
                console.log({ deleteError: e })
                ShowToast({ type: "error", message: message.serverError })
            })
            .finally(() => setSaving(false))
    }

    const handleCreate = () => {
        if (!newDescription.trim()) return
        setSaving(true)
        MicroGoalService.create({ description: newDescription.trim(), remarks: newRemarks })
            .then(() => {
                ShowToast({ type: "success", message: "Micro-objectif créé avec succès" })
                setCreateModalVisible(false)
                setNewDescription("")
                setNewRemarks("")
                createEditorRef.current?.setContentHTML("")
                fetchMicroGoals()
            })
            .catch((e) => {
                console.log({ createError: e })
                ShowToast({ type: "error", message: message.serverError })
            })
            .finally(() => setSaving(false))
    }

    const handleOpenStatusModal = (goal) => {
        setSelectedGoal(goal)
        setSliderValue(statusToSlider(goal.status))
        setStatusModalVisible(true)
    }

    const handleSliderChange = (value) => {
        setSliderValue(Math.round(value))
    }

    const handleSaveStatus = () => {
        const status = sliderToStatus(sliderValue)
        if (status === selectedGoal?.status) {
            setStatusModalVisible(false)
            return
        }
        setSaving(true)
        MicroGoalService.updateStatus(selectedGoal.id, { status })
            .then(() => {
                ShowToast({ type: "success", message: "Statut mis à jour" })
                setStatusModalVisible(false)
                setSelectedGoal(null)
                fetchMicroGoals()
            })
            .catch((e) => {
                console.log({ statusError: e })
                ShowToast({ type: "error", message: message.serverError })
            })
            .finally(() => setSaving(false))
    }

    const renderGoalItem = ({ item }) => {
        let remarksHtml = item.remarks || "";
        if (remarksHtml) {
            try {
                const parsed = JSON.parse(remarksHtml);
                if (parsed && typeof parsed === "object") {
                    if (parsed.blocks && Array.isArray(parsed.blocks)) {
                        remarksHtml = parsed.blocks.map(b => `<p style="margin:0;padding:2px 0;">${b.text}</p>`).join("");
                    } else {
                        remarksHtml = parsed.html || parsed.text || remarksHtml;
                    }
                } else if (typeof parsed === "string") {
                    remarksHtml = parsed;
                }
            } catch (e) {
                // Not a JSON string, use as is
            }
        }

        return (
            <View style={styles.goalCard}>
                <View style={styles.headerRow}>
                    <TouchableOpacity
                        style={[styles.statusDotButton, { backgroundColor: statusColors[item.status] || baseColors.grey }]}
                        onPress={() => handleOpenStatusModal(item)}
                    />
                    <OpenSansText fontSize={14} style={styles.goalDescription}>
                        {item.description}
                    </OpenSansText>

                    <View style={styles.actionsRow}>
                        <TouchableOpacity style={styles.actionButton} onPress={() => handleEdit(item)}>
                            <MaterialIcons name="edit" size={20} color={baseColors.green.primary} />
                        </TouchableOpacity>

                        <TouchableOpacity style={styles.actionButton} onPress={() => handleDelete(item)}>
                            <MaterialIcons name="delete" size={20} color="#E74C3C" />
                        </TouchableOpacity>
                    </View>
                </View>

                {item.remarks ? (
                    <View style={styles.remarksRow}>
                        <OpenSansText fontSize={12} style={styles.remarksLabel} color={"#555"}>
                            Remarques
                        </OpenSansText>
                        <View style={styles.remarksContentContainer}>
                            <ScrollView style={{ maxHeight: 100 }} nestedScrollEnabled={true}>
                                <RenderHTML
                                    contentWidth={width * 0.65}
                                    source={{ html: remarksHtml }}
                                    tagsStyles={{
                                        p: { margin: 0, padding: 0, lineHeight: 18 }
                                    }}
                                    baseStyle={{ fontSize: 13, color: "#333", margin: 0, padding: 0 }}
                                />
                            </ScrollView>
                        </View>
                    </View>
                ) : null}
            </View>
        )
    }

    return (
        <ContainerStackScreen>
            <Spacer height={15} />
            <BreeSerifText color={baseColors.green.primary} fontSize={24}>
                Mes micro-objectifs
            </BreeSerifText>

            <Spacer height={15} />

            <OpenSansText fontSize={16}>Liste de vos micro-objectifs personnels</OpenSansText>

            <Spacer height={15} />

            <Button
                text="+ Nouveau micro-objectif"
                backgroundColor={baseColors.green.primary}
                labelColor={baseColors.white}
                onPress={() => setCreateModalVisible(true)}
                marginTop={0}
            />

            <Spacer height={15} />

            {loading ? (
                <ActivityIndicator color={baseColors.green.primary} size="large" />
            ) : microGoals.length === 0 ? (
                <OpenSansText fontSize={16} textAlign="center" style={{ marginTop: 30 }}>
                    Aucun micro-objectif à afficher
                </OpenSansText>
            ) : (
                <FlatList
                    data={microGoals}
                    keyExtractor={(item) => item.id.toString()}
                    renderItem={renderGoalItem}
                    ItemSeparatorComponent={() => (
                        <Divider style={{ marginVertical: 5, backgroundColor: baseColors.green.light }} />
                    )}
                    contentContainerStyle={{ paddingBottom: 100 }}
                    showsVerticalScrollIndicator={false}
                />
            )}

            {/* Modal de modification */}
            <CustomSimpleModal
                open={editModalVisible}
                toggleModal={() => setEditModalVisible(false)}
                title="Modifier le micro-objectif"
                fontSize={22}
            >
                <OpenSansText fontSize={14}>Nom</OpenSansText>
                <TextInput
                    style={[styles.textInput, { minHeight: 40 }, { marginTop: 10 }]}
                    value={editDescription}
                    onChangeText={setEditDescription}
                    placeholder="Mon micro-objectif"
                    placeholderTextColor="gray"
                />
                <Spacer height={15} />
                <OpenSansText fontSize={14}>Remarques</OpenSansText>
                <View style={{ flexShrink: 1, marginTop: 10 }}>
                    {isWeb ? (
                        <div
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            style={{
                                border: `1px solid ${baseColors.green.dark}`,
                                background: "white",
                                padding: 20,
                                borderRadius: 10,
                                width: "100%",
                                boxSizing: "border-box",
                                display: "flex",
                                flexDirection: "column"
                            }}
                        >
                            <ReactQuill
                                value={editRemarks}
                                onChange={setEditRemarks}
                                modules={quillModules}
                                placeholder=""
                            />
                        </div>
                    ) : (
                        <View style={styles.wysiwygContainer}>
                            <RichToolbar
                                editor={editEditorRef}
                                actions={[
                                    actions.setBold,
                                    actions.setItalic,
                                    actions.setUnderline,
                                    actions.insertBulletsList,
                                    actions.insertOrderedList
                                ]}
                                style={styles.richToolbar}
                            />
                            <View style={styles.richEditorContainer}>
                                <RichEditor
                                    ref={editEditorRef}
                                    initialContentHTML={editRemarks}
                                    onChange={setEditRemarks}
                                    placeholder=""
                                    style={styles.richEditor}
                                    editorStyle={{
                                        backgroundColor: "#fff",
                                        color: baseColors.black,
                                        placeholderColor: baseColors.grey,
                                        contentCSSText: "font-family: sans-serif; font-size: 14px; line-height: 1;"
                                    }}
                                />
                            </View>
                        </View>
                    )}
                </View>
                <Spacer height={10} />
                <Button
                    text="Enregistrer"
                    backgroundColor={baseColors.green.primary}
                    labelColor={baseColors.white}
                    onPress={handleSaveEdit}
                    loading={saving}
                    disabled={!editDescription.trim()}
                    marginTop={5}
                    width="100%"
                />
            </CustomSimpleModal>

            {/* Modal de création */}
            <CustomSimpleModal
                open={createModalVisible}
                toggleModal={() => {
                    setCreateModalVisible(false)
                    setNewDescription("")
                    setNewRemarks("")
                    createEditorRef.current?.setContentHTML("")
                }}
                title="Nouveau micro-objectif"
                fontSize={22}
            >
                <OpenSansText fontSize={14} marginBottom={10}>Nom</OpenSansText>
                <TextInput
                    style={[styles.textInput, { minHeight: 40 }, { marginTop: 10}]}
                    value={newDescription}
                    onChangeText={setNewDescription}
                    placeholder="Mon micro-objectif"
                    placeholderTextColor="gray"
                />
                <Spacer height={15} />
                <OpenSansText fontSize={14}>Remarques</OpenSansText>
                <View style={{ flexShrink: 1, marginTop: 10}}>
                    {isWeb ? (
                        <div
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            style={{
                                border: `1px solid ${baseColors.green.dark}`,
                                background: "white",
                                padding: 20,
                                borderRadius: 10,
                                width: "100%",
                                boxSizing: "border-box",
                                display: "flex",
                                flexDirection: "column"
                            }}
                        >
                            <ReactQuill
                                value={newRemarks}
                                onChange={setNewRemarks}
                                modules={quillModules}
                                placeholder=""
                            />
                        </div>
                    ) : (
                        <View style={styles.wysiwygContainer}>
                            <RichToolbar
                                editor={createEditorRef}
                                actions={[
                                    actions.setBold,
                                    actions.setItalic,
                                    actions.setUnderline,
                                    actions.insertBulletsList,
                                    actions.insertOrderedList
                                ]}
                                style={styles.richToolbar}
                            />
                            <View style={styles.richEditorContainer}>
                                <RichEditor
                                    ref={createEditorRef}
                                    initialContentHTML=""
                                    onChange={setNewRemarks}
                                    placeholder=""
                                    style={styles.richEditor}
                                    editorStyle={{
                                        backgroundColor: "#fff",
                                        color: baseColors.black,
                                        placeholderColor: baseColors.grey,
                                        contentCSSText: "font-family: sans-serif; font-size: 14px; line-height: 1;"
                                    }}
                                />
                            </View>
                        </View>
                    )}
                </View>
                <Spacer height={10} />
                <Button
                    text="Enregistrer"
                    backgroundColor={baseColors.green.primary}
                    labelColor={baseColors.white}
                    onPress={handleCreate}
                    loading={saving}
                    disabled={!newDescription.trim()}
                    marginTop={5}
                    width="100%"
                />
            </CustomSimpleModal>

            {/* Modal de changement de statut */}
            <CustomSimpleModal
                open={statusModalVisible}
                toggleModal={() => setStatusModalVisible(false)}
                title="Changer le statut"
                fontSize={22}
            >
                <View style={styles.sliderRow}>
                    <View style={styles.sliderContainer}>
                        <MultiSlider
                            vertical
                            snapped
                            allowOverlap={false}
                            onValuesChange={(value) => handleSliderChange(value[0])}
                            values={[sliderValue]}
                            touchDimensions={styles.touchDimensions}
                            selectedStyle={{ backgroundColor: baseColors.green.dark }}
                            trackStyle={{
                                backgroundColor: baseColors.green.pastel,
                                height: 10,
                                borderRadius: 5
                            }}
                            markerStyle={{
                                backgroundColor: sliderColor(sliderValue),
                                borderColor: sliderColor(sliderValue),
                                height: 50,
                                width: 20
                            }}
                            min={1}
                            max={5}
                            step={1}
                        />
                    </View>

                    <View style={styles.valuesContainer}>
                        {[...STATUSES].reverse().map((status) => {
                            const hidden = status === "En cours" || status === "Quasi atteint"
                            return (
                                <OpenSansText
                                    key={status}
                                    style={{ color: hidden ? "transparent" : statusColors[status] }}
                                    fontSize={18}
                                >
                                    {status}
                                </OpenSansText>
                            )
                        })}
                    </View>
                </View>

                <Button
                    text="Valider"
                    backgroundColor={baseColors.green.primary}
                    labelColor={baseColors.white}
                    onPress={handleSaveStatus}
                    loading={saving}
                    marginTop={5}
                    width="100%"
                />
            </CustomSimpleModal>

            {/* Modal de confirmation de suppression */}
            <CustomSimpleModal
                open={deleteModalVisible}
                toggleModal={() => setDeleteModalVisible(false)}
                title="Confirmation"
                fontSize={22}
            >
                <OpenSansText fontSize={18} textAlign="center">
                    Voulez-vous vraiment supprimer ce micro-objectif ?
                </OpenSansText>
                <Spacer height={15} />
                <View style={styles.deleteModalButtons}>
                    <Button
                        text="Non"
                        backgroundColor={baseColors.grey}
                        labelColor={baseColors.black}
                        onPress={() => setDeleteModalVisible(false)}
                        marginTop={0}
                        width="45%"
                    />
                    <Button
                        text="Oui"
                        backgroundColor="#E74C3C"
                        labelColor={baseColors.white}
                        onPress={confirmDelete}
                        loading={saving}
                        marginTop={0}
                        width="45%"
                    />
                </View>
            </CustomSimpleModal>
        </ContainerStackScreen>
    )
}

const styles = StyleSheet.create({
    goalCard: {
        backgroundColor: "#F4F7FB", // Similar to image background
        borderRadius: 16,
        padding: 16,
        marginVertical: 4,
        elevation: 0,
    },
    headerRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },
    statusDotButton: {
        width: 14,
        height: 14,
        borderRadius: 7,
        marginRight: 10,
    },
    goalDescription: {
        flex: 1,
        color: "#222",
        fontSize: 16,
    },
    actionsRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        marginLeft: 10,
    },
    actionButton: {
        padding: 2,
    },
    remarksRow: {
        flexDirection: "row",
        marginTop: 15,
        alignItems: "flex-start",
    },
    remarksLabel: {
        width: 80,
        paddingTop: 5,
        fontWeight: "600",
        fontSize: 12,
        color: "#666"
    },
    remarksContentContainer: {
        flex: 1,
        backgroundColor: "white",
        borderRadius: 12,
        padding: 12,
        minHeight: 40,
        justifyContent: "center"
    },
    textInput: {
        borderWidth: 1,
        borderColor: baseColors.grey,
        borderRadius: 8,
        padding: 12,
        fontSize: 16,
        textAlignVertical: "top"
    },
    wysiwygContainer: {
        borderWidth: 1,
        borderColor: baseColors.green.dark,
        backgroundColor: "white",
        padding: 20,
        borderRadius: 10,
        width: "100%"
    },
    richEditorContainer: {
        overflow: "hidden",
        minHeight: 100,
        maxHeight: 150
    },
    richEditor: {
        minHeight: 100,
        maxHeight: 150
    },
    richToolbar: {
        backgroundColor: "#fff",
        marginBottom: 10
    },
    statusOption: {
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 12,
        paddingHorizontal: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "transparent",
        marginVertical: 3
    },
    statusDot: {
        width: 12,
        height: 12,
        borderRadius: 6,
        marginRight: 10
    },
    deleteModalButtons: {
        flexDirection: "row",
        justifyContent: "space-between"
    },
    sliderRow: {
        flexDirection: "row",
        alignItems: "center",
        marginVertical: 10
    },
    sliderContainer: {
        justifyContent: "center",
        alignItems: "center",
        width: "30%"
    },
    valuesContainer: {
        flexDirection: "column",
        justifyContent: "space-between",
        marginLeft: 20,
        height: 290
    },
    touchDimensions: {
        height: 25,
        width: 25,
        borderRadius: 10
    }
})
